import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Search, Package, BarChart3, Grid3X3 } from 'lucide-react';
import { toast } from 'sonner';
import { DateRangePicker } from '@/components/DateRangePicker';
import { ExportButton } from '@/components/ExportButton';
import { GeoFilters } from '@/components/GeoFilters';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const formatINR = (v) => {
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v?.toLocaleString('en-IN') || '0';
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="text-xs font-heading font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs text-muted-foreground">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: p.color }} />
          {p.name}: {typeof p.value === 'number' ? formatINR(p.value) : p.value}
        </p>
      ))}
    </div>
  );
}

export default function ProductsPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [view, setView] = useState('category');
  const [category, setCategory] = useState('');
  const [zone, setZone] = useState('');
  const [subzone, setSubzone] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  // Drilldown
  const [drilldown, setDrilldown] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownData, setDrilldownData] = useState([]);

  useEffect(() => {
    setLoading(true);
    const params = { view };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (zone) params.zone = zone;
    if (subzone) params.subzone = subzone;
    if (state) params.state = state;
    if (city) params.city = city;
    if (category && view === 'sku') params.category = category;
    api.get('/products/crosstab', { params }).then(r => setData(r.data)).catch(() => toast.error('Failed')).finally(() => setLoading(false));
  }, [api, view, dateFrom, dateTo, zone, subzone, state, city, category]);

  const handleDrilldown = async (item, zoneKey) => {
    setDrilldown({ name: item.name || item.sku_code, zone: zoneKey });
    setDrilldownLoading(true);
    try {
      const params = { zone: zoneKey };
      if (view === 'sku') params.sku_code = item.sku_code;
      else params.category = item.name;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get('/products/drilldown', { params });
      setDrilldownData(res.data.outlets || []);
    } catch { toast.error('Failed to load details'); }
    finally { setDrilldownLoading(false); }
  };

  const filteredData = (data?.data || []).filter(item => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (item.name || '').toLowerCase().includes(q) || (item.sku_code || '').toLowerCase().includes(q) || (item.category || '').toLowerCase().includes(q);
  });

  const allZones = data?.zones || [];
  const chartData = filteredData.slice(0, 12).map(d => ({ name: (d.name || d.sku_code || '').substring(0, 20), amount: d.total_amount, outlets: d.total_outlets }));

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Products</h1>
          <p className="text-sm text-muted-foreground mt-1">Category and SKU performance across territories</p>
        </div>
        <ExportButton module="distribution" params={{ zone, subzone, state, city, date_from: dateFrom, date_to: dateTo }} label="Export" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex gap-0.5 bg-muted/50 rounded-md p-0.5">
          <button onClick={() => setView('category')} className={`px-3 py-1.5 text-xs font-heading font-medium rounded flex items-center gap-1 ${view === 'category' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="view-category">
            <Grid3X3 size={12} /> Category
          </button>
          <button onClick={() => setView('sku')} className={`px-3 py-1.5 text-xs font-heading font-medium rounded flex items-center gap-1 ${view === 'sku' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`} data-testid="view-sku">
            <Package size={12} /> SKU
          </button>
        </div>
        {view === 'sku' && data?.categories && (
          <Select value={category || 'all'} onValueChange={v => setCategory(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-44" data-testid="filter-category"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Categories</SelectItem>{data.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <GeoFilters api={api} zone={zone} setZone={setZone} subzone={subzone} setSubzone={setSubzone} state={state} setState={setState} city={city} setCity={setCity} />
        <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} />
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder={view === 'category' ? 'Search category...' : 'Search SKU...'} value={search} onChange={e => setSearch(e.target.value)} className="pl-8 w-52" data-testid="product-search" />
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">{view === 'category' ? 'Categories' : 'SKUs'}</p>
          <p className="text-xl font-heading font-bold mt-1">{filteredData.length}</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Zones</p>
          <p className="text-xl font-heading font-bold mt-1">{allZones.length}</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Total Outlets</p>
          <p className="text-xl font-heading font-bold mt-1">{filteredData.reduce((s, d) => s + d.total_outlets, 0).toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Total Amount</p>
          <p className="text-xl font-heading font-bold mt-1">INR {formatINR(filteredData.reduce((s, d) => s + d.total_amount, 0))}</p>
        </div>
      </div>

      {/* Chart */}
      <div className="bg-card border border-border rounded-md p-5">
        <h3 className="font-heading font-semibold text-sm mb-4">Top {view === 'category' ? 'Categories' : 'SKUs'} by Order Amount</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={formatINR} stroke="hsl(var(--muted-foreground))" />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={140} stroke="hsl(var(--muted-foreground))" />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="amount" name="Amount (INR)" fill="#1B3A8C" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Crosstab Table */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground text-sm flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading...</div>
      ) : (
        <div className="bg-card border border-border rounded-md overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 sticky top-0">
              <tr>
                <th className="text-left py-2.5 px-3 text-xs font-heading text-muted-foreground min-w-[180px] sticky left-0 bg-muted/30 z-10">{view === 'category' ? 'Category' : 'SKU'}</th>
                <th className="text-right py-2.5 px-3 text-xs font-heading text-muted-foreground min-w-[80px]">Total</th>
                {allZones.map(z => (
                  <th key={z} className="text-right py-2.5 px-2 text-[10px] font-heading text-muted-foreground min-w-[80px]">{z}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, i) => (
                <tr key={i} className="border-t border-border/30 hover:bg-muted/20">
                  <td className="py-2 px-3 sticky left-0 bg-card z-10">
                    <div>
                      <p className="font-medium text-sm">{item.name || item.sku_code}</p>
                      {view === 'sku' && item.category && <p className="text-[10px] text-muted-foreground">{item.category}</p>}
                    </div>
                  </td>
                  <td className="text-right py-2 px-3">
                    <div>
                      <p className="font-medium text-xs">{formatINR(item.total_amount)}</p>
                      <p className="text-[10px] text-muted-foreground">{item.total_outlets.toLocaleString('en-IN')} outlets</p>
                    </div>
                  </td>
                  {allZones.map(z => {
                    const cell = item.zones?.[z];
                    return (
                      <td key={z} className="text-right py-2 px-2">
                        {cell ? (
                          <button onClick={() => handleDrilldown(item, z)} className="text-right w-full hover:bg-primary/5 rounded px-1 py-0.5 transition-colors">
                            <p className="text-xs font-medium">{formatINR(cell.amount)}</p>
                            <p className="text-[10px] text-muted-foreground">{cell.outlets} outlets</p>
                          </button>
                        ) : (
                          <span className="text-[10px] text-muted-foreground/30">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {filteredData.length === 0 && (
                <tr><td colSpan={allZones.length + 2} className="text-center py-8 text-muted-foreground">No data found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Drilldown Dialog */}
      <Dialog open={!!drilldown} onOpenChange={v => { if (!v) setDrilldown(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-auto" onInteractOutside={e => e.preventDefault()} data-testid="drilldown-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{drilldown?.name} — {drilldown?.zone}</DialogTitle>
          </DialogHeader>
          {drilldownLoading ? (
            <div className="text-center py-8"><Loader2 size={20} className="animate-spin mx-auto text-primary" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Outlet</TableHead>
                  <TableHead className="text-xs">Beat</TableHead>
                  <TableHead className="text-right text-xs">SKUs</TableHead>
                  <TableHead className="text-right text-xs">Amount</TableHead>
                  <TableHead className="text-right text-xs">Fulfilled</TableHead>
                  <TableHead className="text-right text-xs">Orders</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drilldownData.map((o, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-sm font-medium">{o.outlet_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{o.beat}</TableCell>
                    <TableCell className="text-right text-sm">{o.sku_count}</TableCell>
                    <TableCell className="text-right text-sm">{formatINR(o.amount)}</TableCell>
                    <TableCell className="text-right text-sm">{formatINR(o.fulfilled)}</TableCell>
                    <TableCell className="text-right text-sm">{o.orders}</TableCell>
                  </TableRow>
                ))}
                {drilldownData.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No outlets found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
